import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Container, Typography, Button, Collapse, CircularProgress, Box, Dialog, DialogTitle, DialogContent, TextField, Checkbox, FormControlLabel, Snackbar, Alert, Chip, Rating } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useGoogleLogin } from '@react-oauth/google';
import { loadStripe } from '@stripe/stripe-js';
import SitemapComparison from './components/SitemapComparison';
import PageDistribution from './components/PageDistribution';
import ChatInterface from './components/ChatInterface';
import GameModal from './components/GameModal';
import Header from './components/Header';
import ReportSelector from './components/ReportSelector';
import QuickLLMSGenerator from './components/QuickLLMSGenerator';
import { useJobPolling } from './hooks/useJobPolling';
import { useAnalysisPolling } from './hooks/useAnalysisPolling';
import './App.css';

const BACKEND = 'https://lobster-app-sd3s8.ondigitalocean.app';

const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const SnapshotContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
}));

const SnapshotHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const ReportSelectorContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

const UsageChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

const ElegantDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '600px',
    maxWidth: '90%',
    borderRadius: '12px',
    background: '#ffffff',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    padding: theme.spacing(3),
    border: '1px solid #e2e8f0',
  },
}));

const CenteredDialogTitle = styled(DialogTitle)(({ theme }) => ({
  textAlign: 'center',
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1a202c',
  fontFamily: "'Source Serif Pro', serif",
  paddingBottom: theme.spacing(1),
}));

const ElegantTypography = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: '#4a5568',
  fontFamily: "'Inter', sans-serif",
  lineHeight: 1.6,
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const RatingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  margin: `${theme.spacing(3)} 0`,
  backgroundColor: '#f7fafc',
  padding: theme.spacing(2),
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
}));

const FeedbackTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    '&:hover fieldset': {
      borderColor: '#cbd5e0',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2563eb',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#718096',
    fontFamily: "'Inter', sans-serif",
  },
}));

const SurveyActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: theme.spacing(1, 3),
  fontSize: '1rem',
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  textTransform: 'none',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}));

// Function to normalize domains (e.g., https://www.example.com/ → example.com)
const normalizeDomain = (domain) => {
  if (!domain || typeof domain !== 'string') return domain;
  // Remove http://, https://, and www.
  let normalized = domain
    .replace(/^https?:\/\//, '') // Remove protocol
    .replace(/^www\./, '')       // Remove www.
    .trim();                     // Remove whitespace
  // Remove trailing slashes
  normalized = normalized.replace(/\/+$/, '');
  return normalized;
};

export default function Dashboard({ token, user, handleLogout }) {
  const [sitemapTrees, setSitemapTrees] = useState([]);
  const [analysis, setAnalysis] = useState('');
  const [analysisTimestamp, setAnalysisTimestamp] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [jobId, setJobId] = useState(null);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [retryAction, setRetryAction] = useState(null);
  const [showGame, setShowGame] = useState(false);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(true);
  const [chatHistory, setChatHistory] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
  const [expandedSnapshots, setExpandedSnapshots] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyRating, setSurveyRating] = useState(null);
  const [surveyFeedback, setSurveyFeedback] = useState('');
  const [reportFormData, setReportFormData] = useState({
    name: '',
    domain: '',
    competitorDomains: ['', ''],
    weeklyEmail: true,
  });
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [reports, setReports] = useState([]);
  const [localUser, setLocalUser] = useState(user);
  const chartRefs = useRef({});
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [isNewReport, setIsNewReport] = useState(false);
  const focusReturnRef = useRef(null);
  const dialogRef = useRef(null);
  const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  let stripePromise;
  try {
    if (!STRIPE_KEY) {
      throw new Error('Stripe Publishable Key is missing. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in the .env file.');
    }
    stripePromise = loadStripe(STRIPE_KEY);
  } catch (error) {
    console.error('Failed to initialize Stripe:', error);
    stripePromise = null;
  }

  useEffect(() => {
    setLocalUser(user);
  }, [user]);

  const handleError = useCallback((err, retryCallback = null) => {
    console.error('Error:', err);
    const message = err.response?.data?.error?.message || err?.error?.message || err.message || 'An unexpected error occurred.';
    const code = err.response?.data?.error?.code || err?.error?.code || 'UnknownError';
    const status = err.response?.status || err?.error?.status || 500;
    const action = err.response?.data?.error?.action || err?.error?.action;

    if (status === 401 && code === 'AuthenticationError') {
      setSnackbarMessage(message || 'Your session has expired. Please log in again.');
      setSnackbarOpen(true);
      localStorage.removeItem('jwt');
      handleLogout();
    } else if (status === 429 && code === 'RateLimitError') {
      setSnackbarMessage(message || 'Rate limit exceeded. Please try again later.');
      setRetryAction(() => retryCallback);
      setSnackbarOpen(true);
    } else if (status === 404 && code === 'SitemapNotFoundError') {
      setSnackbarMessage(message || 'Sitemap not found. Please check the domain and try again.');
      setSnackbarOpen(true);
    } else if (status === 403 && code === 'UsageLimitExceeded' && action === 'showSurvey') {
      if (!localUser.hasCompletedSurvey) {
        setShowSurveyModal(true);
      }
    } else {
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    }
  }, [localUser, handleLogout]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
    setRetryAction(null);
  };

  const handleRetry = () => {
    if (retryAction) {
      retryAction();
      setRetryAction(null);
      setSnackbarOpen(false);
    }
  };

  const handleSurveySubmit = async () => {
    if (!surveyRating) return;
    try {
      const res = await fetch(`${BACKEND}/api/survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rating: surveyRating })
      });
      if (!res.ok) {
        const data = await res.json();
        throw data;
      }
      if (surveyRating <= 3 && surveyFeedback.trim()) {
        const feedbackRes = await fetch(`${BACKEND}/api/user/feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ feedback: surveyFeedback })
        });
        if (!feedbackRes.ok) {
          const feedbackData = await feedbackRes.json();
          throw feedbackData;
        }
      }
      const userRes = await fetch(`${BACKEND}/api/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!userRes.ok) {
        const userData = await userRes.json();
        throw userData;
      }
      const userData = await userRes.json();
      setLocalUser({
        ...localUser,
        hasCompletedSurvey: userData.hasCompletedSurvey,
        surveyRating: userData.surveyRating,
        feedback: userData.feedback
      });
    } catch (err) {
      handleError(err);
    }
  };

  const handleSubscribe = async () => {
    setIsSubscribing(true);
    try {
      if (!stripePromise) {
        throw new Error('Stripe.js failed to initialize. Please check your network or Stripe configuration.');
      }
      await handleSurveySubmit();
      const priceId = 'price_1RUFpVPvdfDOuTO9JeJdiffu';
      const res = await fetch(`${BACKEND}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ priceId })
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const data = await res.json();
          throw data;
        }
        throw new Error('Server returned an unexpected response');
      }
      const data = await res.json();
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe.js failed to load.');
      }
      const { error } = await stripe.redirectToCheckout({
        sessionId: data.sessionId
      });
      if (error) {
        throw new Error(error.message || 'Failed to redirect to Stripe Checkout.');
      }
    } catch (err) {
      handleError(err);
      setIsSubscribing(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/customer-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const data = await res.json();
          throw data;
        }
        throw new Error('Server returned an unexpected response');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const contentType = res.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            const data = await res.json();
            throw data;
          }
          throw new Error('Server returned an unexpected response');
        }
        const data = await res.json();
        setReports(data.reports || []);
        if (!data.reports || data.reports.length === 0) {
          setShowReportForm(true);
        }
      } catch (err) {
        handleError(err);
      }
    };
    if (token) {
      fetchReports();
    }
  }, [token, handleError]);

  useEffect(() => {
    const loadGapi = () => {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.onload = () => {
        window.gapi.load('client', () => {
          Promise.all([
            window.gapi.client.load('https://docs.googleapis.com/$discovery/rest?version=v1'),
            window.gapi.client.load('https://sheets.googleapis.com/$discovery/rest?version=v4')
          ])
            .then(() => setGapiLoaded(true))
            .catch(err => {
              handleError({ message: 'Failed to load Google APIs: ' + (err.message || 'Unknown error') });
              console.error('GAPI error:', err);
            });
        });
      };
      script.onerror = () => {
        handleError({ message: 'Failed to load Google API Client Library' });
      };
      document.body.appendChild(script);
    };
    loadGapi();
  }, [handleError]);

  useEffect(() => {
    if (showReportForm && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [showReportForm]);

  const googleLogin = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/documents https://www.googleapis.com/auth/spreadsheets',
    onSuccess: (tokenResponse) => {
      console.log('Google API access token:', tokenResponse.access_token);
      setAccessToken(tokenResponse.access_token);
    },
    onError: (err) => {
      handleError({ message: 'Failed to authenticate with Google APIs: ' + (err.error || 'Unknown error') });
      console.error('Google login error:', err);
    },
    flow: 'implicit'
  });

  const fetchSitemapData = useCallback(async sid => {
    if (!sid) return;
    try {
      console.log('Fetching sitemap data for sessionId:', sid);
      const res = await fetch(`${BACKEND}/api/sitemaps/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const data = await res.json();
          throw data;
        }
        throw new Error('Server returned an unexpected response');
      }
      const data = await res.json();
      const { sitemapTrees, analysis, analysisTimestamp } = data;
      console.log('fetchSitemapData response:', { sessionId: sid, sitemapTrees, analysis, analysisTimestamp });
      
      // Deduplicate sitemapTrees by domain
      const uniqueSitemapTrees = [];
      const seenDomains = new Set();
      for (const tree of sitemapTrees || []) {
        if (!seenDomains.has(tree.domain)) {
          seenDomains.add(tree.domain);
          uniqueSitemapTrees.push(tree);
        }
      }
  
      setSitemapTrees(uniqueSitemapTrees);
      if (analysis) {
        setAnalysis(analysis);
        setAnalysisTimestamp(analysisTimestamp ? new Date(analysisTimestamp) : null);
      } else {
        setAnalysis('');
        setAnalysisTimestamp(null);
      }
    } catch (err) {
      handleError(err, () => fetchSitemapData(sid));
    }
  }, [token, handleError]);

  const handleJobPollingComplete = useCallback(({ sessionId }) => {
    console.log('useJobPolling onComplete triggered with sessionId:', sessionId);
    setSessionId(sessionId);
    setIsNewReport(false);
    setJobId(null);
    fetchSitemapData(sessionId);
  }, [fetchSitemapData]);

  const handleJobPollingError = useCallback((err) => {
    console.error('useJobPolling error:', err);
    handleError({ message: err || 'Failed to poll job status' });
    setJobId(null);
    setIsNewReport(false);
  }, [handleError]);

  const { isPolling } = useJobPolling(jobId, token, {
    onComplete: handleJobPollingComplete,
    onError: handleJobPollingError
  });

  const handleSelectReport = useCallback((report) => {
    setSelectedReport(report);
    setSitemapTrees([]);
    setAnalysis('');
    setAnalysisTimestamp(null);
    setSessionId('');
    setSnapshots([]);
    setChatHistory([]);
    setIsNewReport(false);
    if (report && report.lastFullAnalysisSessionId) {
      setSessionId(report.lastFullAnalysisSessionId);
      fetchSitemapData(report.lastFullAnalysisSessionId);
    }
  }, [fetchSitemapData]);

  useEffect(() => {
    if (selectedReport && selectedReport.lastFullAnalysisSessionId && !isNewReport && sessionId !== selectedReport.lastFullAnalysisSessionId) {
      fetchSitemapData(selectedReport.lastFullAnalysisSessionId);
    }
  }, [selectedReport, isNewReport, fetchSitemapData, sessionId]);

  const handleAddReport = useCallback(() => {
    setReportFormData({
      name: '',
      domain: '',
      competitorDomains: ['', ''],
      weeklyEmail: true,
    });
    setShowReportForm(true);
  }, []);

  const handleReportFormChange = useCallback((field, value) => {
    setReportFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCompetitorChange = useCallback((idx, val) => {
    const copy = [...reportFormData.competitorDomains];
    copy[idx] = val;
    setReportFormData(prev => ({ ...prev, competitorDomains: copy }));
  }, [reportFormData.competitorDomains]);

  const handleReportSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSitemapTrees([]);
    setAnalysis('');
    setAnalysisTimestamp(null);
    setSessionId('');
    setSnapshots([]);
    setChatHistory([]);
    try {
      // Normalize the domain and competitor domains
      const normalizedDomain = normalizeDomain(reportFormData.domain.trim());
      const normalizedCompList = reportFormData.competitorDomains
        .map(domain => normalizeDomain(domain.trim()))
        .filter(c => c); // Remove empty strings after normalization

      const res = await fetch(`${BACKEND}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: reportFormData.name.trim() || 'New Report',
          domain: normalizedDomain,
          competitorDomains: normalizedCompList,
          notificationPreferences: { weeklyEmail: reportFormData.weeklyEmail }
        })
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const data = await res.json();
          throw data;
        }
        throw new Error('Server returned an unexpected response');
      }
      const data = await res.json();
      const { report, sessionId, jobId } = data;
      if (!jobId) {
        throw new Error('Backend did not return a jobId');
      }
      console.log('handleReportSubmit extracted:', { report, sessionId, jobId });
      setReports(prev => [...prev, report]);
      setSelectedReport(report);
      setSessionId(sessionId);
      setJobId(jobId);
      setIsNewReport(true);
      setShowReportForm(false);
    } catch (err) {
      handleError(err, handleReportSubmit);
      setIsNewReport(false);
    }
  }, [reportFormData, token, handleError]);

  const fetchSnapshots = useCallback(async (reportId) => {
    if (!reportId) return;
    try {
      const res = await fetch(`${BACKEND}/api/snapshots/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const data = await res.json();
          throw data;
        }
        throw new Error('Server returned an unexpected response');
      }
      const data = await res.json();
      setSnapshots(data.snapshots || []);
    } catch (err) {
      handleError(err, () => fetchSnapshots(reportId));
    }
  }, [token, handleError]);

  useEffect(() => {
    if (selectedReport) {
      fetchSnapshots(selectedReport._id);
    }
  }, [selectedReport, fetchSnapshots]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      window.history.replaceState({}, document.title, window.location.pathname);
      const refreshUserData = async () => {
        try {
          const userRes = await fetch(`${BACKEND}/api/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!userRes.ok) {
            const contentType = userRes.headers.get('content-type');
            if (contentType?.includes('application/json')) {
              const data = await userRes.json();
              throw data;
            }
            throw new Error('Server returned an unexpected response');
          }
          const userData = await userRes.json();
          setLocalUser({
            ...localUser,
            hasCompletedSurvey: userData.hasCompletedSurvey,
            reportsCreated: userData.reportsCreated,
            questionsAsked: userData.questionsAsked,
            stripeSubscriptionId: userData.stripeSubscriptionId,
            subscriptionStatus: userData.subscriptionStatus,
            surveyRating: userData.surveyRating,
            feedback: userData.feedback
          });
          setShowSurveyModal(false);
        } catch (err) {
          handleError(err);
        }
      };
      refreshUserData();
    }
  }, [token, handleError, localUser]);

  const [snapshotJobId, setSnapshotJobId] = useState(null);

  const handleTakeSnapshot = useCallback(async () => {
    if (!selectedReport || !sessionId) {
      handleError({ message: 'No report or session selected.' });
      return;
    }
    setIsSnapshotLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/snapshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reportId: selectedReport._id, sessionId })
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const data = await res.json();
          throw data;
        }
        throw new Error('Server returned an unexpected response');
      }
      const data = await res.json();
      const { jobId } = data;
      setSnapshotJobId(jobId);
    } catch (err) {
      handleError(err, handleTakeSnapshot);
      setIsSnapshotLoading(false);
    }
  }, [selectedReport, sessionId, token, handleError]);
  
  useJobPolling(snapshotJobId, token, {
    onComplete: ({ snapshot }) => {
      if (snapshot) {
        setSnapshots([snapshot, ...snapshots]);
      }
      setSnapshotJobId(null);
      setIsSnapshotLoading(false);
    },
    onError: (err) => {
      handleError(err, () => handleTakeSnapshot());
      setSnapshotJobId(null);
      setIsSnapshotLoading(false);
    }
  });

  const toggleSnapshot = (index) => {
    setExpandedSnapshots(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  useAnalysisPolling(analysisJobId, token, {
    onComplete: (result) => {
      console.log('Analysis result:', result);
      if (result && result.analysis) {
        setAnalysis(result.analysis);
        setAnalysisTimestamp(new Date().toISOString());
        setIsAnalysisLoading(false);
      } else {
        handleError({ message: 'Analysis result is missing or incomplete.' });
        setIsAnalysisLoading(false);
      }
    },
    onError: (err) => {
      handleError(err);
      setIsAnalysisLoading(false);
    },
  });

  const handleAnalyze = useCallback(async () => {
    if (!selectedReport || !sessionId) {
      handleError({ message: 'No report or session selected.' });
      setIsAnalysisLoading(false);
      return;
    }
    setIsAnalysisLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/sitemaps/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const data = await res.json();
          throw data;
        }
        throw new Error('Server returned an unexpected response');
      }
      const data = await res.json();
      const { analysis, analysisTimestamp } = data;
      console.log('handleAnalyze check:', { sessionId, analysis, analysisTimestamp });
      if (analysis) {
        setAnalysis(analysis);
        setAnalysisTimestamp(analysisTimestamp ? new Date(analysisTimestamp) : null);
        setIsAnalysisLoading(false);
        return;
      }
      const analyzeRes = await fetch(`${BACKEND}/api/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reportId: selectedReport._id, sessionId })
      });
      if (!analyzeRes.ok) {
        const contentType = analyzeRes.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const data = await analyzeRes.json();
          throw data;
        }
        throw new Error('Server returned an unexpected response');
      }
      const analyzeData = await analyzeRes.json();
      const { jobId } = analyzeData;
      setAnalysisJobId(jobId);
    } catch (err) {
      handleError(err, handleAnalyze);
      setIsAnalysisLoading(false);
    }
  }, [selectedReport, sessionId, token, handleError]);

  const getPieChartData = useCallback((pieChartData, totalCount) => {
    if (!pieChartData || !pieChartData.folders || !Array.isArray(pieChartData.folders)) {
      return {
        pieChartData: { labels: [], datasets: [{ data: [], backgroundColor: [] }] },
        pieChartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right' },
            tooltip: { callbacks: { label: () => '' } }
          }
        },
        totalCategories: 0
      };
    }
    const cats = [...pieChartData.folders];
    if (pieChartData.otherCount) cats.push({ name: 'Other', count: pieChartData.otherCount });
    const labels = cats.map(c => c.name || '');
    const data = cats.map(c => c.count || 0);
    const cyberpunkColors = ['#1a4d3a', '#059669', '#2563eb', '#7c3aed', '#ea580c']; // Professional green theme
    const bg = cats.map((_, i) => cyberpunkColors[i % cyberpunkColors.length]); // Cycle through colors
    return {
      pieChartData: { labels, datasets: [{ data, backgroundColor: bg }] },
      pieChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right' },
          tooltip: {
            callbacks: {
              label: ctx => {
                const v = ctx.raw || 0;
                const pct = ((v / (totalCount || 1)) * 100).toFixed(1);
                return `${ctx.label}: ${v} pages (${pct}%)`;
              }
            }
          }
        }
      },
      totalCategories: cats.length
    };
  }, []);

  const generateReport = useCallback(async () => {
    if (!gapiLoaded) {
      handleError('Google Docs API not loaded yet. Please try again.');
      return;
    }
    if (!accessToken) {
      googleLogin();
      return;
    }
    try {
      window.gapi.client.setToken({ access_token: accessToken });
      const createResponse = await window.gapi.client.docs.documents.create({
        title: `SEO Analysis Report - ${selectedReport?.name || 'Untitled'}`
      });
      const documentId = createResponse.result.documentId;
      const requests = [];
      let currentIndex = 1;
      const addText = (text, style = null) => {
        requests.push({
          insertText: { location: { index: currentIndex }, text }
        });
        const textLength = text.length;
        if (style) {
          requests.push({
            updateParagraphStyle: {
              range: { startIndex: currentIndex, endIndex: currentIndex + textLength - 1 },
              paragraphStyle: { namedStyleType: style },
              sx: 'namedStyleType'
            }
          });
        }
        currentIndex += textLength;
      };
      addText(`SEO Analysis Report - ${selectedReport?.name || 'Untitled'}\n\n`, 'HEADING_1');
      if (analysis) {
        addText('Analysis\n', 'HEADING_2');
        addText(analysis + '\n\n');
      }
      await window.gapi.client.docs.documents.batchUpdate({ documentId, requests });
      window.open(`https://docs.google.com/document/d/${documentId}/edit`, '_blank');
    } catch (err) {
      handleError({ message: 'Error generating report: ' + (err.result?.error?.message || err.message) });
      console.error('Docs API error:', err);
    }
  }, [gapiLoaded, accessToken, selectedReport, analysis, googleLogin, handleError]);

  const exportChatToGoogleDocs = useCallback(async () => {
    if (!gapiLoaded) {
      handleError({ message: 'Google Docs API not loaded yet. Please try again.' });
      return;
    }
    if (!accessToken) {
      googleLogin();
      return;
    }
    try {
      window.gapi.client.setToken({ access_token: accessToken });
      const createResponse = await window.gapi.client.docs.documents.create({
        title: `SEO Chat History - ${selectedReport?.name || 'Untitled'}`
      });
      const documentId = createResponse.result.documentId;
      const requests = [];
      let currentIndex = 1;
      const addText = (text, style = null) => {
        requests.push({
          insertText: { location: { index: currentIndex }, text }
        });
        const textLength = text.length;
        if (style) {
          requests.push({
            updateParagraphStyle: {
              range: { startIndex: currentIndex, endIndex: currentIndex + textLength - 1 },
              paragraphStyle: { namedStyleType: style },
              fields: 'namedStyleType'
            }
          });
        }
        currentIndex += textLength;
      };
      addText(`SEO Chat History - ${selectedReport?.name || 'Untitled'}\n\n`, 'HEADING_1');
      if (chatHistory.length > 0) {
        addText('Chat History\n', 'HEADING_2');
        const chatText = chatHistory.map((entry, index) => {
          return `**Q${index + 1}:** ${entry.question}\n\n**A${index + 1}:**\n${entry.answer}\n\n`;
        }).join('');
        addText(chatText);
      }
      await window.gapi.client.docs.documents.batchUpdate({ documentId, requests });
      window.open(`https://docs.google.com/document/d/${documentId}/edit`, '_blank');
    } catch (err) {
      handleError({ message: 'Error exporting chat to Google Docs: ' + (err.result?.error?.message || err.message) });
      console.error('Docs API error:', err);
    }
  }, [gapiLoaded, accessToken, selectedReport, chatHistory, googleLogin, handleError]);

  const isSitemapLoading = isNewReport || isPolling;

  console.log('Dashboard render - isSitemapLoading:', isSitemapLoading, 'isNewReport:', isNewReport, 'isPolling:', isPolling);

  return (
    <Container className="App">
      <Header user={localUser} handleLogout={handleLogout} />
      <Typography variant="h1" align="center" gutterBottom>
        SiteMind Research Beta
      </Typography>
      <Box display="flex" justifyContent="center" mb={2}>
        {!localUser.hasCompletedSurvey && !localUser.stripeSubscriptionId && (
          <>
            <UsageChip
              label={`Reports: ${localUser.reportsCreated}/${localUser.freePlanLimits.reports}`}
              color={localUser.reportsCreated >= localUser.freePlanLimits.reports ? 'error' : 'primary'}
            />
            <UsageChip
              label={`Questions: ${localUser.questionsAsked}/${localUser.freePlanLimits.questions}`}
              color={localUser.questionsAsked >= localUser.freePlanLimits.questions ? 'error' : 'primary'}
            />
          </>
        )}
        {localUser.stripeSubscriptionId && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleManageSubscription}
            sx={{ ml: 2 }}
          >
            Manage Subscription
          </Button>
        )}
      </Box>
      <ReportSelectorContainer>
        <ReportSelector
          token={token}
          reports={reports}
          onSelectReport={handleSelectReport}
          onAddReport={handleAddReport}
        />
      </ReportSelectorContainer>
      
      <ContentContainer>
        {isSitemapLoading && (
          <LoadingContainer>
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
              Magic is happening!
            </Typography>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="h6" align="center">
              Sites are being crawled, indexed and analyzed
            </Typography>
          </LoadingContainer>
        )}
        {sitemapTrees.length > 0 && !isSitemapLoading && (
          <>
            <SitemapComparison
              trees={sitemapTrees}
              googleAccessToken={accessToken}
              reAuth={googleLogin}
              gapiLoaded={gapiLoaded}
              handleError={handleError}
            />
            <Typography variant="h2">Page Distribution by Category</Typography>
            <PageDistribution
              trees={sitemapTrees}
              getChart={getPieChartData}
              ref={(instance) => {
                if (instance && instance.current) {
                  Object.keys(instance.current).forEach(domain => {
                    chartRefs.current[domain] = instance.current[domain];
                  });
                }
              }}
            />
            {!analysis && (
              <ActionButton
                variant="contained"
                color="primary"
                onClick={handleAnalyze}
                disabled={isAnalysisLoading}
                startIcon={isAnalysisLoading && <CircularProgress size={20} color="inherit" />}
              >
                {isAnalysisLoading ? 'Analyzing...' : 'Analyze Sitemaps'}
              </ActionButton>
            )}
            {analysis && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Typography variant="h2">Final Analysis</Typography>
                  <Button
                    onClick={() => setIsAnalysisExpanded(prev => !prev)}
                    sx={{ textTransform: 'none' }}
                  >
                    {isAnalysisExpanded ? 'Hide Analysis' : 'Show Analysis'}
                  </Button>
                </Box>
                <Collapse in={isAnalysisExpanded}>
                  {analysisTimestamp && (
                    <Typography variant="body2" color="textSecondary">
                      Last updated: {analysisTimestamp.toLocaleDateString()}
                    </Typography>
                  )}
                  <div className="analysis-container">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{analysis}</ReactMarkdown>
                  </div>
                  <ActionButton variant="contained" color="secondary" onClick={generateReport}>
                    Export Report to Google Docs
                  </ActionButton>
                  <ActionButton 
                    variant="contained" 
                    color="primary" 
                    onClick={() => document.querySelector('[data-llms-generator]')?.scrollIntoView({ behavior: 'smooth' })}
                    sx={{ ml: 1 }}
                  >
                    Generate LLMS.txt Files
                  </ActionButton>
                </Collapse>
              </>
            )}
            <ChatInterface
              sessionId={sessionId}
              reportId={selectedReport?._id}
              disabled={!selectedReport || !sessionId}
              token={token}
              onChatHistoryChange={setChatHistory}
              handleError={handleError}
            />
            {chatHistory.length > 0 && (
              <ActionButton
                variant="contained"
                color="secondary"
                onClick={exportChatToGoogleDocs}
              >
                Export Chat to Google Docs
              </ActionButton>
            )}
            
            {/* Quick LLMS.txt Generator */}
            <QuickLLMSGenerator token={token} selectedReport={selectedReport} />
            {snapshots.length > 0 && (
              <SnapshotContainer id="snapshots">
                <SnapshotHeader>
                  <Typography variant="h2">Historical Snapshots</Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleTakeSnapshot}
                    disabled={isSnapshotLoading}
                    startIcon={isSnapshotLoading && <CircularProgress size={20} color="inherit" />}
                  >
                    {isSnapshotLoading ? 'Taking Snapshot...' : 'Take Snapshot'}
                  </Button>
                </SnapshotHeader>
                {snapshots
                  .filter(snapshot => snapshot && snapshot.timestamp) // Filter out invalid snapshots
                  .map((snapshot, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body1">
                            Snapshot {index + 1} - {new Date(snapshot.timestamp).toLocaleString()}
                          </Typography>
                          <Typography variant="body2">
                            Your Domain: Added: {snapshot.userChanges?.added?.length || 0}, Removed: {snapshot.userChanges?.removed?.length || 0}
                          </Typography>
                          <Typography variant="body2">
                            Competitor Domains: {snapshot.competitorChanges?.map(c => `${c.domain}: Added: ${c.added?.length || 0}, Removed: ${c.removed?.length || 0}`).join('; ') || 'None'}
                          </Typography>
                        </Box>
                        {snapshot.aiAnalysis && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => toggleSnapshot(index)}
                            sx={{ ml: 1 }}
                          >
                            {expandedSnapshots[index] ? 'Hide Analysis' : 'View Analysis'}
                          </Button>
                        )}
                      </Box>
                      {snapshot.aiAnalysis && (
                        <Collapse in={expandedSnapshots[index]}>
                          <div className="analysis-container">
                            <Typography variant="h3">AI Analysis</Typography>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{snapshot.aiAnalysis}</ReactMarkdown>
                          </div>
                        </Collapse>
                      )}
                    </Box>
                  ))}
              </SnapshotContainer>
            )}
            
            <div className="game-launch-container">
              <ActionButton variant="contained" onClick={() => setShowGame(true)}>
                Play a Game While You Wait
              </ActionButton>
            </div>
            <GameModal
              open={showGame}
              onClose={() => setShowGame(false)}
              closeButtonProps={{
                sx: {
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  backgroundColor: '#f7fafc',
                  color: '#4a5568',
                  '&:hover': { backgroundColor: '#e2e8f0' },
                  boxShadow: 1,
                  fontWeight: 500,
                },
              }}
            />
          </>
        )}
      </ContentContainer>
      <Button
        ref={focusReturnRef}
        style={{ position: 'absolute', left: '-9999px' }}
        onClick={() => {}}
        aria-hidden="true"
      >
        Focus return point
      </Button>
      <Dialog
        open={showReportForm}
        onClose={() => {
          setShowReportForm(false);
          focusReturnRef.current?.focus();
        }}
        aria-labelledby="create-report-dialog-title"
        ref={dialogRef}
        tabIndex={-1}
      >
        <DialogTitle id="create-report-dialog-title">Create New Report</DialogTitle>
        <DialogContent>
          <TextField
            label="Report Name"
            placeholder="e.g., My SEO Report"
            fullWidth
            value={reportFormData.name}
            onChange={(e) => handleReportFormChange('name', e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label="Your Domain"
            placeholder="example.com"
            fullWidth
            required
            value={reportFormData.domain}
            onChange={(e) => handleReportFormChange('domain', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle1" gutterBottom>
            Up to 2 Competitor Domains (optional)
          </Typography>
          {reportFormData.competitorDomains.map((c, i) => (
            <TextField
              key={i}
              label={`Competitor #${i + 1}`}
              placeholder="competitor.com"
              fullWidth
              value={c}
              onChange={(e) => handleCompetitorChange(i, e.target.value)}
              sx={{ mb: 2 }}
            />
          ))}
          <FormControlLabel
            control={
              <Checkbox
                checked={reportFormData.weeklyEmail}
                onChange={(e) => handleReportFormChange('weeklyEmail', e.target.checked)}
              />
            }
            label="Receive weekly competitor updates for this report"
          />
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleReportSubmit}
              sx={{ mr: 1 }}
            >
              Create Report
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setShowReportForm(false);
                focusReturnRef.current?.focus();
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <ElegantDialog
        open={showSurveyModal}
        onClose={() => {
          setShowSurveyModal(false);
          setSurveyRating(null);
          setSurveyFeedback('');
        }}
        aria-labelledby="survey-modal-title"
      >
        <CenteredDialogTitle id="survey-modal-title">
          We Value Your Feedback
        </CenteredDialogTitle>
        <DialogContent>
          <ElegantTypography variant="body1">
            You've reached the limits of our free plan. Before we proceed, we'd love to hear your thoughts.
          </ElegantTypography>
          <ElegantTypography variant="body1">
            How likely are you to recommend our tool to a colleague or friend?
          </ElegantTypography>
          <RatingContainer>
            <Rating
              name="survey-rating"
              value={surveyRating}
              onChange={(event, newValue) => setSurveyRating(newValue)}
              size="large"
              sx={{
                '& .MuiRating-icon': {
                  color: '#2563eb',
                },
                '& .MuiRating-iconEmpty': {
                  color: '#e2e8f0',
                },
              }}
            />
          </RatingContainer>
          <ElegantTypography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
            (1 = Not Likely, 5 = Very Likely)
          </ElegantTypography>
          {surveyRating !== null && surveyRating <= 3 && (
            <>
              <ElegantTypography variant="body1">
                Thank you for your honesty. What could we improve to better meet your needs?
              </ElegantTypography>
              <FeedbackTextField
                fullWidth
                multiline
                rows={4}
                value={surveyFeedback}
                onChange={(e) => setSurveyFeedback(e.target.value)}
                placeholder="Share your thoughts..."
                variant="outlined"
                sx={{ mb: 3 }}
              />
            </>
          )}
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <SurveyActionButton
              variant="contained"
              onClick={handleSubscribe}
              disabled={isSubscribing || surveyRating === null}
              startIcon={isSubscribing && <CircularProgress size={20} />}
              sx={{
                backgroundColor: '#2563eb',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                },
              }}
            >
              {isSubscribing ? 'Processing...' : 'Continue to Subscribe'}
            </SurveyActionButton>
            <ActionButton
              variant="outlined"
              onClick={() => {
                setShowSurveyModal(false);
                setSurveyRating(null);
                setSurveyFeedback('');
              }}
              disabled={isSubscribing}
              sx={{
                borderColor: '#e2e8f0',
                color: '#4a5568',
                '&:hover': {
                  borderColor: '#cbd5e0',
                  color: '#2d3748',
                  backgroundColor: '#f7fafc',
                },
              }}
            >
              Cancel
            </ActionButton>
          </Box>
        </DialogContent>
      </ElegantDialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        action={
          retryAction && (
            <Button color="inherit" onClick={handleRetry}>
              Retry
            </Button>
          )
        }
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}